/**
 * Lejárat-tudatos hiba-szövegek a MENTETLEN-VÉDELMES űrlapokhoz (web).
 * [Web UX 2. csomag – 3. javító kör, validátor F-1, 2026-10-03]
 *
 * MI VOLT A BAJ: lejáratkor (mentetlen adat → újra-belépő ablak „a változásaid megmaradtak") az
 * új 12 védett űrlap mentő ága a nyers „… (HTTP 401)" szöveget írta (mérve:
 * `ComposeMessageDialog.tsx` „Nem sikerült elküldeni az üzenetet. (HTTP 401)"), mert csak az
 * `apiErrorMessage`-et (vagy a még régebbi `err.response?.data?.message || '…'` mintát) hívta.
 * Az `EventCreateDialog` pedig a fordított hibát követte el: MINDEN 401-re a „megmaradtak" mondatot
 * írta, a zár vizsgálata nélkül — egy elavult (közben lecserélt tokenű) 401 is ezt kapta, ablak nélkül.
 *
 * A JAVÍTÁS: két burkoló pár, mindkettő a lejárt-ZÁRAT nézi (`utils/session.ts`):
 *   - `saveErrorMessage` / `saveResponseErrorMessage`: MENTÉS/KÜLDÉS hibája → zárral a
 *     `SESSION_EXPIRED_SAVE_MESSAGE` („… a változásaid megmaradtak …"), különben a szokásos szöveg;
 *   - `requestErrorMessage` / `requestResponseErrorMessage`: BETÖLTÉS vagy nem-űrlap művelet
 *     hibája → zárral a rövid `SESSION_EXPIRED_MESSAGE`, különben a szokásos szöveg.
 * (Az axios-pár az `apiErrorMessage`-re, a `fetch`-pár a `responseErrorMessage`-re épül.)
 *
 * [7. javító kör — validátor F2, 2026-10-04] A „megmaradtak" mondat a zár MELLETT csak akkor jár,
 * ha az újra-belépő ablak TÉNYLEGESEN nyitva van (`isReauthDialogOpen`). Mentetlen adat nélkül nincs
 * ablak (azonnali kijelentkeztetés) — ott a rövid `SESSION_EXPIRED_MESSAGE` az igaz mondat, és a
 * `FeedbackProvider` ezt a toastot el is nyeli (`shouldSuppressSessionToast`), mert a belépő oldal
 * ugyanezt mondja: a `/belepes`-en EGY üzenet legyen.
 *
 * MIÉRT NEM az `apiErrors.ts`-ben: annak magja a mobil tükörrel közös, a paritás-őr
 * (`utils/__tests__/apiErrors.parity.test.ts`) ugyanazt a viselkedést kéri mindkét oldalon, és a
 * belépő űrlap 401-e (rossz jelszó) a szerver magyar indoklását kell mutassa — ezt a
 * `sessionExpiredSaveMessage` az auth-URL kizárásával és a zárral védi.
 *
 * ŐR: `components/__tests__/unsavedCoverage.test.ts` — minden `useUnsavedChanges(`-t hívó komponens
 * CSAK ezeken a burkolókon át adhat kérés-hiba szöveget (a nyers `apiErrorMessage(`,
 * `responseErrorMessage(`, `sessionExpiredSaveMessage(`, `SESSION_EXPIRED_SAVE_MESSAGE` és
 * `err.response?.data?.message` ott tiltott).
 */
import { apiErrorMessage, responseErrorMessage } from './apiErrors';
import {
  isReauthDialogOpen,
  isSessionExpiredLocked,
  sessionExpiredSaveMessage,
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_SAVE_MESSAGE,
} from './session';

/** Axios-os MENTÉS/KÜLDÉS hibája: zárral a „megmaradtak" mondat, különben `apiErrorMessage`. */
export function saveErrorMessage(err: any, fallback: string): string {
  return sessionExpiredSaveMessage(err) ?? apiErrorMessage(err, fallback);
}

/** Axios-os BETÖLTÉS / nem-űrlap művelet hibája: zárral a rövid lejárat-mondat. */
export function requestErrorMessage(err: any, fallback: string): string {
  return sessionExpiredSaveMessage(err) ? SESSION_EXPIRED_MESSAGE : apiErrorMessage(err, fallback);
}

/**
 * `fetch`-es MENTÉS hibája. A hívó a `reportFetchUnauthorized(response, token)`-t a válasz UTÁN,
 * ezelőtt hívja — az állítja a zárat a saját tokenje 401-ére; egy elavult token 401-e nem állítja,
 * így ott a szokásos „… (HTTP 401)" marad (nincs hamis „megmaradtak").
 */
export function saveResponseErrorMessage(rawBody: string | undefined | null, status: number, fallback: string): string {
  if (status === 401 && isSessionExpiredLocked()) {
    // [7. javító kör — validátor F2] „megmaradtak" CSAK nyitott újra-belépő ablaknál igaz
    return isReauthDialogOpen() ? SESSION_EXPIRED_SAVE_MESSAGE : SESSION_EXPIRED_MESSAGE;
  }
  return responseErrorMessage(rawBody, status, fallback);
}

/** `fetch`-es BETÖLTÉS hibája (lásd `saveResponseErrorMessage`). */
export function requestResponseErrorMessage(rawBody: string | undefined | null, status: number, fallback: string): string {
  if (status === 401 && isSessionExpiredLocked()) return SESSION_EXPIRED_MESSAGE;
  return responseErrorMessage(rawBody, status, fallback);
}
